import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { DModal, DNumberField, DYear, DDatePicker } from '../../../../../dum';
import { useAppDispatch } from '../../../../../hooks';
import { pushMessage } from '../../../../../../features/global-messages/global-messages-slice';
import {
    useRecurringExpenseCandidatesLazyQuery,
    useGenerateRecurringExpensesMutation,
    useExpenseReceiptTypeIdInputQuery,
} from '../../../../../../services';
import { formatFloat, DATE_FORMAT } from '../../../../../../helpers';
import dayjs from 'dayjs';

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    // Prefill the target month (Resumen de gastos opens this dialog on the
    // month it flagged as missing). Defaults to the current month. These seed
    // component state, so callers that need to retarget an already-mounted
    // dialog must remount it with a `key` — which is what that page does.
    initialYear?: number;
    // 1-indexed, matching the dialog's own month state.
    initialMonth?: number;
}

interface CandidateRow {
    expense_id: number;
    account_name: string;
    // Source expense date (drives the "Origen" month label).
    date: string;
    // New expense's date + payment date (suggested, user-editable), DATE_FORMAT.
    expense_date: string;
    payment_date: string;
    receipt_type_id: number | null;
    already_generated: boolean;
    checked: boolean;
    tax: number;
    tax_retained: number;
    non_tax_retained: number;
    lines: {
        resource_id: number | null;
        units: number;
        unit_price: number;
        notes: string;
        date: string | null;
    }[];
}

function getSourceMonthLabel(dateStr: string): string {
    const d = new Date(dateStr);
    const months = [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
    ];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

function computeTotal(
    lines: { units: number; unit_price: number }[],
    tax: number,
    tax_retained: number,
    non_tax_retained: number,
): number {
    const subtotal = lines.reduce((sum, l) => sum + l.units * l.unit_price, 0);
    return subtotal + tax - tax_retained - non_tax_retained;
}

export function SGenerateRecurringExpensesDialog({
    open,
    setOpen,
    initialYear,
    initialMonth,
}: IProps) {
    const dispatch = useAppDispatch();
    const now = new Date();
    const [year, setYear] = useState(initialYear ?? now.getFullYear());
    const [month, setMonth] = useState(initialMonth ?? now.getMonth() + 1);

    const { data: receiptTypesData } = useExpenseReceiptTypeIdInputQuery();
    const receiptTypeAppliesTax = useMemo(() => {
        const map = new Map<number, boolean>();
        (receiptTypesData?.getReceiptTypes ?? []).forEach((rt) => {
            map.set(rt.id, rt.applies_tax);
        });
        return map;
    }, [receiptTypesData]);
    const appliesTax = useCallback(
        (receiptTypeId: number | null | undefined) =>
            !!receiptTypeId && !!receiptTypeAppliesTax.get(receiptTypeId),
        [receiptTypeAppliesTax],
    );

    const [fetchCandidates, { loading: loadingCandidates }] =
        useRecurringExpenseCandidatesLazyQuery({
            fetchPolicy: 'network-only',
        });
    const [generateMutation, { loading: generating }] =
        useGenerateRecurringExpensesMutation();

    const [rows, setRows] = useState<CandidateRow[]>([]);

    const loadCandidates = useCallback(async () => {
        const { data } = await fetchCandidates({
            variables: { year, month },
        });
        if (data?.getRecurringExpenseCandidates) {
            // New expenses default to the 15th of the selected month; the
            // payment date defaults to the backend's transfer-based suggestion
            // (falling back to the expense date when there is none). Both are
            // editable per candidate below.
            const defaultExpenseDate = dayjs(
                new Date(year, month - 1, 15),
            ).format(DATE_FORMAT);
            setRows(
                data.getRecurringExpenseCandidates.map((c) => ({
                    expense_id: c.expense_id,
                    account_name: c.account_name,
                    date: c.date,
                    expense_date: defaultExpenseDate,
                    payment_date: c.suggested_payment_date
                        ? dayjs(c.suggested_payment_date).format(DATE_FORMAT)
                        : defaultExpenseDate,
                    receipt_type_id: c.receipt_type_id ?? null,
                    already_generated: c.already_generated,
                    checked: !c.already_generated,
                    tax: appliesTax(c.receipt_type_id) ? c.tax : 0,
                    tax_retained: c.tax_retained,
                    non_tax_retained: c.non_tax_retained,
                    lines: c.expense_resources.map((r) => ({
                        resource_id: r.resource_id ?? null,
                        units: r.units,
                        unit_price: r.unit_price ?? 0,
                        notes: r.notes,
                        date: r.date ?? null,
                    })),
                })),
            );
        }
    }, [fetchCandidates, year, month, appliesTax]);

    useEffect(() => {
        if (open) {
            void loadCandidates();
        }
    }, [open, loadCandidates]);

    const handleToggle = (idx: number) => {
        setRows((prev) =>
            prev.map((r, i) =>
                i === idx ? { ...r, checked: !r.checked } : r,
            ),
        );
    };

    const handleLineChange = (
        rowIdx: number,
        lineIdx: number,
        field: 'units' | 'unit_price',
        value: string,
    ) => {
        setRows((prev) =>
            prev.map((r, ri) => {
                if (ri !== rowIdx) return r;
                const lines = r.lines.map((l, li) => {
                    if (li !== lineIdx) return l;
                    return { ...l, [field]: Number(value) || 0 };
                });
                return { ...r, lines };
            }),
        );
    };

    const handleDateChange = (
        rowIdx: number,
        field: 'expense_date' | 'payment_date',
        value: string | null,
    ) => {
        if (!value) return;
        setRows((prev) =>
            prev.map((r, ri) =>
                ri === rowIdx ? { ...r, [field]: value } : r,
            ),
        );
    };

    const handleTaxChange = (
        rowIdx: number,
        field: 'tax' | 'tax_retained' | 'non_tax_retained',
        value: string,
    ) => {
        setRows((prev) =>
            prev.map((r, ri) => {
                if (ri !== rowIdx) return r;
                if (field === 'tax' && !appliesTax(r.receipt_type_id)) return r;
                return { ...r, [field]: Number(value) || 0 };
            }),
        );
    };

    const handleGenerate = async () => {
        const selected = rows.filter((r) => r.checked && !r.already_generated);
        if (selected.length === 0) return;

        try {
            const { data } = await generateMutation({
                variables: {
                    input: selected.map((r) => ({
                        source_expense_id: r.expense_id,
                        date: dayjs(r.expense_date, DATE_FORMAT).toDate(),
                        expected_payment_date: dayjs(
                            r.payment_date,
                            DATE_FORMAT,
                        ).toDate(),
                        tax: r.tax,
                        tax_retained: r.tax_retained,
                        non_tax_retained: r.non_tax_retained,
                        expense_resources: r.lines.map((l) => ({
                            resource_id: l.resource_id,
                            units: l.units,
                            unit_price: l.unit_price,
                            notes: l.notes,
                            date: l.date ? new Date(l.date) : null,
                        })),
                    })),
                },
            });

            const created = data?.generateRecurringExpenses.created_ids.length ?? 0;
            const skipped = data?.generateRecurringExpenses.skipped_ids.length ?? 0;

            dispatch(
                pushMessage({
                    message: `${created} gasto(s) creado(s)${skipped > 0 ? `, ${skipped} omitido(s)` : ''}`,
                    options: { variant: 'success' },
                }),
            );

            setOpen(false);
        } catch (e: any) {
            dispatch(
                pushMessage({
                    message: e?.message || 'Error al generar gastos',
                    options: { variant: 'error' },
                }),
            );
        }
    };

    const monthOptions = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ];

    const checkedCount = rows.filter(
        (r) => r.checked && !r.already_generated,
    ).length;

    return (
        <DModal
            title="Generar gastos recurrentes"
            open={open}
            setOpen={setOpen}
            width="900px"
        >
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        select
                        label="Mes"
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                        size="small"
                        margin="dense"
                        sx={{ minWidth: 140 }}
                    >
                        {monthOptions.map((name, i) => (
                            <MenuItem key={i} value={i + 1}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ width: 150 }}>
                        <DYear
                            year={year}
                            setYear={(y) => {
                                if (y !== null) {
                                    setYear(y);
                                }
                            }}
                            clearable={false}
                        />
                    </Box>
                </Box>

                {loadingCandidates ? (
                    <Typography>Cargando candidatos...</Typography>
                ) : rows.length === 0 ? (
                    <Typography color="text.secondary">
                        No hay candidatos para este mes.
                    </Typography>
                ) : (
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell align="left">Proveedor</TableCell>
                                    <TableCell align="left">Origen</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="left">Estado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, rowIdx) => {
                                    const total = computeTotal(row.lines, row.tax, row.tax_retained, row.non_tax_retained);
                                    return (
                                        <React.Fragment key={row.expense_id}>
                                            <TableRow
                                                sx={{
                                                    opacity: row.already_generated
                                                        ? 0.5
                                                        : 1,
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={row.checked}
                                                        disabled={row.already_generated}
                                                        onChange={() =>
                                                            handleToggle(rowIdx)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell align="left">
                                                    {row.account_name}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {getSourceMonthLabel(row.date)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${formatFloat(total)}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {row.already_generated ? (
                                                        <Chip
                                                            label="Ya generado"
                                                            size="small"
                                                            color="default"
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                            {row.checked &&
                                                !row.already_generated && (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={5}
                                                            sx={{
                                                                py: 2,
                                                                px: 4,
                                                                bgcolor:
                                                                    'action.hover',
                                                            }}
                                                        >
                                                            {/* Fechas del gasto y del pago (sugeridas, editables) */}
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    flexWrap:
                                                                        'wrap',
                                                                    gap: 1.5,
                                                                    mb: 1.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 190,
                                                                    }}
                                                                >
                                                                    <DDatePicker
                                                                        label={
                                                                            'Fecha del gasto'
                                                                        }
                                                                        value={
                                                                            row.expense_date
                                                                        }
                                                                        views={[
                                                                            'year',
                                                                            'month',
                                                                            'day',
                                                                        ]}
                                                                        inputFormat={
                                                                            DATE_FORMAT
                                                                        }
                                                                        clearable={
                                                                            false
                                                                        }
                                                                        disableGutters
                                                                        onChange={(
                                                                            value,
                                                                        ) =>
                                                                            handleDateChange(
                                                                                rowIdx,
                                                                                'expense_date',
                                                                                value,
                                                                            )
                                                                        }
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        width: 190,
                                                                    }}
                                                                >
                                                                    <DDatePicker
                                                                        label={
                                                                            'Fecha de pago'
                                                                        }
                                                                        value={
                                                                            row.payment_date
                                                                        }
                                                                        views={[
                                                                            'year',
                                                                            'month',
                                                                            'day',
                                                                        ]}
                                                                        inputFormat={
                                                                            DATE_FORMAT
                                                                        }
                                                                        clearable={
                                                                            false
                                                                        }
                                                                        disableGutters
                                                                        onChange={(
                                                                            value,
                                                                        ) =>
                                                                            handleDateChange(
                                                                                rowIdx,
                                                                                'payment_date',
                                                                                value,
                                                                            )
                                                                        }
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            {/* Cantidad · Precio · Subtotal por línea + impuestos, en una sola fila */}
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    flexWrap:
                                                                        'wrap',
                                                                    gap: 1.5,
                                                                    alignItems:
                                                                        'flex-end',
                                                                }}
                                                            >
                                                                {row.lines.map(
                                                                    (
                                                                        line,
                                                                        lineIdx,
                                                                    ) => (
                                                                        <React.Fragment
                                                                            key={
                                                                                lineIdx
                                                                            }
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 95,
                                                                                }}
                                                                            >
                                                                                <DNumberField
                                                                                    label={
                                                                                        'Cantidad'
                                                                                    }
                                                                                    value={
                                                                                        line.units
                                                                                    }
                                                                                    disableGutters
                                                                                    onChange={(
                                                                                        value,
                                                                                        source,
                                                                                    ) => {
                                                                                        if (
                                                                                            source ===
                                                                                            'event'
                                                                                        ) {
                                                                                            handleLineChange(
                                                                                                rowIdx,
                                                                                                lineIdx,
                                                                                                'units',
                                                                                                value,
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 115,
                                                                                }}
                                                                            >
                                                                                <DNumberField
                                                                                    label={
                                                                                        'Precio'
                                                                                    }
                                                                                    value={
                                                                                        line.unit_price
                                                                                    }
                                                                                    disableGutters
                                                                                    onChange={(
                                                                                        value,
                                                                                        source,
                                                                                    ) => {
                                                                                        if (
                                                                                            source ===
                                                                                            'event'
                                                                                        ) {
                                                                                            handleLineChange(
                                                                                                rowIdx,
                                                                                                lineIdx,
                                                                                                'unit_price',
                                                                                                value,
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 125,
                                                                                }}
                                                                            >
                                                                                <DNumberField
                                                                                    label={
                                                                                        'Subtotal'
                                                                                    }
                                                                                    value={
                                                                                        line.units *
                                                                                        line.unit_price
                                                                                    }
                                                                                    disabled
                                                                                    disableGutters
                                                                                    onChange={() =>
                                                                                        undefined
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                        </React.Fragment>
                                                                    ),
                                                                )}
                                                                <Box
                                                                    sx={{
                                                                        width: 115,
                                                                    }}
                                                                >
                                                                    <DNumberField
                                                                        label={
                                                                            'IVA'
                                                                        }
                                                                        value={
                                                                            row.tax
                                                                        }
                                                                        disabled={
                                                                            !appliesTax(
                                                                                row.receipt_type_id,
                                                                            )
                                                                        }
                                                                        disableGutters
                                                                        onChange={(
                                                                            value,
                                                                            source,
                                                                        ) => {
                                                                            if (
                                                                                source ===
                                                                                'event'
                                                                            ) {
                                                                                handleTaxChange(
                                                                                    rowIdx,
                                                                                    'tax',
                                                                                    value,
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        width: 125,
                                                                    }}
                                                                >
                                                                    <DNumberField
                                                                        label={
                                                                            'IVA retenido'
                                                                        }
                                                                        value={
                                                                            row.tax_retained
                                                                        }
                                                                        disableGutters
                                                                        onChange={(
                                                                            value,
                                                                            source,
                                                                        ) => {
                                                                            if (
                                                                                source ===
                                                                                'event'
                                                                            ) {
                                                                                handleTaxChange(
                                                                                    rowIdx,
                                                                                    'tax_retained',
                                                                                    value,
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        width: 125,
                                                                    }}
                                                                >
                                                                    <DNumberField
                                                                        label={
                                                                            'ISR retenido'
                                                                        }
                                                                        value={
                                                                            row.non_tax_retained
                                                                        }
                                                                        disableGutters
                                                                        onChange={(
                                                                            value,
                                                                            source,
                                                                        ) => {
                                                                            if (
                                                                                source ===
                                                                                'event'
                                                                            ) {
                                                                                handleTaxChange(
                                                                                    rowIdx,
                                                                                    'non_tax_retained',
                                                                                    value,
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                        gap: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(false)}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            generating || checkedCount === 0 || loadingCandidates
                        }
                        onClick={handleGenerate}
                    >
                        Generar ({checkedCount})
                    </Button>
                </Box>
            </Box>
        </DModal>
    );
}
